import { defineStore } from 'pinia';
import LoginService from "../service/LoginService";
import { Routes } from '../router';
const loginService = new LoginService();
export const useLoginStore = defineStore({
  id: 'login',
  state: () => ({
    accessToken: null,
    expiresIn: null,
    refreshToken: null,
    idToken: null,
    decoded_jwt: null,
    decoded_username: null,
    username: null,
    password: null,
    session: null,
    token_ioCentro: {
      accessToken: null,
      refreshToken: null,
      tenantAdminUserCode: null
    },
    abac_config: null,
    accessDeniedToggle: false,
    tenant: "",
    source: null,
    personalInfo: {
      userId: null,
      firstName: null,
      lastName: null,
      role: null,
      address: null,
      zip: null,
      city: null,
      state: null,
      country: null,
      email: null,
      mobile: null,
      customerName: null
    },
  }),
  getters: {
    getTenant: (state) => {
      return state.tenant;
    },
    getAccessDeniedToggle: (state) => {
      return state.accessDeniedToggle;
    },
    getExpiresIn: (state) => {
      return state.expiresIn;
    },
    getAccessToken: (state) => {
      return state.accessToken;
    },
    getRefreshToken: (state) => {
      return state.refreshToken;
    },
    getDecodedJWT: (state) => {
      return state.decoded_jwt;
    },
    getDecodedUsername: (state) => {
      return state.decoded_username;
    },
    getUsername: (state) => {
      return state.username;
    },
    getPassword: (state) => {
      return state.password
    },
    getSession: (state) => {
      return state.session
    },
    getAbacConfig: (state) => {
      return state.abac_config
    },
    getAbacAnalyticsConfig: (state) => {
      if (state.abac_config?.role_def?.analytics) {
        return state.abac_config.role_def.analytics;
      }
      return null;
    },
    getAbacAnalyticsSingleApplianceConfig: (state) => {
      if (state.abac_config?.role_def?.single_device_analytics) {
        return state.abac_config.role_def.single_device_analytics;
      }
      return null;
    },
    getSource: (state) => {
      return state.source;
    },
    getPersonalInfo: (state) => {
      return state.personalInfo;
    },
  },
  actions: {
    setSource(source) {
      this.source = source;
    },
    setTenant(tenant) {
      this.tenant = tenant.key;
    },
    checkRoutePermission(path) {
      let permitted = false;
      if (path.substring(0, 4) === "/ota" && this.abac_config.role_def.ota) {
        permitted = true;
      } else if (path.substring(0, 10) === "/analytics") {
        if (this.abac_config.role_def.analytics && this.abac_config.role_def.analytics.sections) {
          this.abac_config.role_def.analytics.sections.forEach(element => {
            if (this.setDefaultLabel(element.id) === path.substring(11)) {
              permitted = true;
            }
          });
        }
        if (!permitted && this.abac_config.role_def.single_device_analytics) {
          for (let i in this.abac_config.role_def.single_device_analytics) {
            let sub_end_index = ((path.substring(11)).indexOf('/') + 11);
            if (this.abac_config.role_def.single_device_analytics[path.substring(11, sub_end_index)]) {
              permitted = true;
            }
          }
        }
      } else {
        permitted = true;
      }

      return permitted;
    },
    parseJwt(token) {
      var base64Url = token.split('.')[1];
      var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    },
    parseUser(token) {
      return JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).name
    },
    checkExpiresDate() {
      let jwt = new Date(this.decoded_jwt.exp * 1000);
      let curr = new Date();
      return (jwt <= curr);
    },
    async setAbacConfig(tenant = "smegcons") {
      await loginService.abac(this.getAccessToken).then((abac_config) => {
        this.abac_config = abac_config;
      });
    },
    addRoutesMenu(router) {
      const defaultPath = {
        path: '/',
        name: 'default',
        redirect: this.getAbacConfig?.role_def?.device ? Routes.APPLIANCES : Routes.USERS,
        meta: {
            requiresAuth: true
        },
      };
      router.addRoute(defaultPath);

      if(this.getAbacConfig?.role_def?.device) {
        const appliances = {
          path: Routes.APPLIANCES,
          name: 'Appliances',
          component: () => import('../pages/Appliances.vue'),
          meta: {
            requiresAuth: true
          },
        };
        router.addRoute(appliances);
      }

      if(this.getAbacConfig?.role_def?.device?.single_view) {
        const single_appliance = {
          path: '/appliances/:id',
          name: 'singleAppliance',
          component: () => import('../pages/SingleAppliance.vue'),
          meta: {
            requiresAuth: true
          },
        };
        router.addRoute(single_appliance);
      }

      if(this.getAbacConfig?.role_def?.user) {
        const users = {
          path: '/users',
          name: 'Users',
          component: () => import('../pages/Users.vue'),
          meta: {
            requiresAuth: true
          },
        };
        router.addRoute(users);
      }

      if(this.getAbacConfig?.role_def?.user?.single_view) {
        const single_user = {
          path: '/users/:id',
          name: 'singleUser',
          component: () => import('../pages/SingleUser.vue'),
          meta: {
            requiresAuth: true
          },
        };
        router.addRoute(single_user);
      }

      let children_analytics = [];
      
      if(this.getAbacConfig?.role_def?.single_device_analytics) {
        children_analytics.push({
          path: ':type/:id',
          exact: true,
          component: () => import('../components/analytics/SingleApplianceAnalyticsSection.vue'),
          name: 'single-appliance-analytics',
        })
      }

      if (this.abac_config?.role_def?.analytics.sections) {
        for (let i = 0; i < this.abac_config.role_def.analytics.sections.length; i++) {
          let analytics_section_name = this.setDefaultLabel(this.abac_config.role_def.analytics.sections[i].id);
          let obj = {
            path: analytics_section_name,
            exact: true,
            component: () => import('../components/analytics/AnalyticsSection.vue'),
            name: analytics_section_name
          }
          children_analytics.push(obj);
        }
      }

      const analytics = {
        path: '/analytics',
        name: 'Analytics',
        exact: true,
        component: () => import('../components/analytics/Charts.vue'),
        meta: {
          requiresAuth: true
        },
        children: children_analytics
      };
      router.addRoute(analytics);

      if (this.getAbacConfig?.role_def?.ota) {
        const ota_campaign = {
          path: '/ota',
          name: 'campaign',
          component: () => import('../pages/Ota.vue'),
          meta: {
            requiresAuth: true
          },
          redirect: '/ota/campaign',
          children: [
            {
              path: 'campaign',
              component: () => import('../pages/OtaListPage.vue'),
              name: 'campaign',
            },
            {
              path: 'campaign/:id',
              component: () => import('../pages/SingleCampaign.vue'),
              name: "campaignOtaDetails"
            },
            {
              path: 'create-campaign',
              component: () => import('../pages/CreateCampaign.vue'),
              name: 'create-campaign',
            },
            {
              path: 'modify-campaign/:id',
              component: () => import('../pages/CreateCampaign.vue'),
              name: 'modify-campaign',
            },
          ]
        };
        router.addRoute(ota_campaign);
      }

      if (this.getAbacConfig?.role_def?.ota) {
        const ota_group = {
          path: '/ota',
          name: 'group',
          component: () => import('../pages/Ota.vue'),
          exact: true,
          meta: {
            requiresAuth: true
          },
          children: [
            {
              path: 'group',
              component: () => import('../pages/OtaListPage.vue'),
              name: 'group',
            },
            {
              path: 'group/:id',
              component: () => import('../pages/SingleGroup.vue'),
              name: "groupOtaDetails"
            },
            {
              path: 'create-group',
              component: () => import('../pages/CreateGroup.vue'),
              name: 'create-group',
            },
            {
              path: 'modify-group/:id',
              component: () => import('../pages/CreateGroup.vue'),
              name: 'modify-group',
            },
          ]
        };
        router.addRoute(ota_group);
      }
      
      if (this.getAbacConfig?.role_def?.reports) {
        const reports = {
          path: '/reports',
          name: 'ReportList',
          component: () => import('../pages/ReportList.vue'),
          meta: {
              requiresAuth: true
          },
        };
        router.addRoute(reports);
      }      
      
      if (this.getAbacConfig?.role_def?.most_visited_pages) {
        const reports = {
          path: '/most-visited-pages',
          name: 'Statistics',
          component: () => import('../pages/Statistics.vue'),
          meta: {
              requiresAuth: true
          },
        };
        router.addRoute(reports);
      }

      if (this.getAbacConfig?.role_def?.reports?.snapshot) {
        const snapshots = {
          path: '/snapshot',
          name: 'Snapshot',
          component: () => import('../components/analytics/AnalyticsSnapshot.vue'),
          meta: {
              requiresAuth: true
          },
        };
        router.addRoute(snapshots);
      }
      
      if (this.getAbacConfig?.role_def?.tags_page) {
        const tags = {
          path: '/tags',
          name: 'Tags',
          component: () => import('../pages/Tags.vue'),
          meta: {
            requiresAuth: true
          },
        };
        router.addRoute(tags);
      }
      
      if (this.getAbacConfig?.role_def?.pending_dealers) {
        const pendingDealers = {
          path: '/pending-dealers',
          name: 'Pending Dealers',
          component: () => import('../pages/PendingDealers.vue'),
          meta: {
            requiresAuth: true
          },
        };
        router.addRoute(pendingDealers);
      }
      
      if (this.getAbacConfig?.role_def?.appliance_verbose) {
        const verboseAppliances = {
          path: '/verbose',
          name: 'verbose_appliances',
          component: () => import('../pages/VerboseAppliances.vue'),
          meta: {
            requiresAuth: true
          },
        };
        router.addRoute(verboseAppliances);
        const verboseSingleAppliance = {
          path: '/verbose/:id',
          name: 'verbose_single_appliance',
          component: () => import('../pages/VerboseSingleAppliance.vue'),
          meta: {
            requiresAuth: true
          },
        };
        router.addRoute(verboseSingleAppliance);
      }
      

    },
    setDefaultLabel(id) {
      // return old_name.replace(/\s+/g, '-').toLowerCase();
      return id;
    },
    // setIoCentroTokens(accessToken, refreshToken, tenantAdminUserCode) {
    //   this.token_ioCentro.accessToken = accessToken;
    //   this.token_ioCentro.refreshToken = refreshToken;
    //   this.token_ioCentro.tenantAdminUserCode = tenantAdminUserCode;
    // },
    setLoginParameters(accessToken, expiresIn, refreshToken, idToken, iotUserCode) {
      this.accessToken = accessToken;
      this.expiresIn = expiresIn;
      this.refreshToken = refreshToken;
      this.idToken = idToken;
      this.decoded_jwt = this.parseJwt(accessToken);
      // this.decoded_username = this.parseUser(idToken);
      this.personalInfo.userId = iotUserCode;
    },
    setCredentialParameters(username, password, session) {
      this.username = username;
      this.password = password;
      this.session = session;
    },
    async init(router) {
      await this.refreshTokenCall(router);
      setInterval(async function () {
        if (this.refreshToken) {
          await this.refreshTokenCall(router);
        }
      }.bind(this), (50*60*1000));
      // }, (this.expiresIn * 1000));
    },
    async logout() {
      if (this.refreshToken) {
        await loginService.logout(this.accessToken).then(() => {
          this.accessToken = this.expiresIn = this.refreshToken = null;
        });
      } else {
        this.accessToken = this.expiresIn = this.refreshToken = this.decoded_jwt = this.idToken = null;
      }
      this.abac_config = null;
    },
    async checkToken(router) {
      if (!this.getDecodedJWT || (!this.getRefreshToken && this.getDecodedJWT.exp < Date.now() / 1000)) {
        await this.logout();
        if(this.tenant!="") {
          router.push({
            name: "login-"+this.tenant
          });
        }
      } else if (this.getDecodedJWT.exp < Date.now() / 1000) {
        await this.refreshTokenCall(router);
      }
    },
    async refreshTokenCall(router) {
      if(this.getRefreshToken) {
        await loginService
            .refresh(this.getRefreshToken)
            .then((data) => {
              this.setLoginParameters(
                data.accessToken,
                86400,
                this.refreshToken,
                this.idToken
              );
            })
            .catch((error) => {
              if (error.response) {
                console.log(error.response.data); // => the response payload
              } else {
                console.log(JSON.stringify(error));
              }
              this.logout();
              router.push({
                name: "login-"+this.tenant
              });
            });
      }
    },
    async fetchPersonalInfo() {
      // try {
        let data = await loginService.fetchPersonalInfo(this.personalInfo.userId, this.accessToken)
          .then((response) => response.user)
        this.personalInfo.firstName = data.firstName;
        this.personalInfo.lastName = data.lastName;
        this.personalInfo.role = data.role;
        this.personalInfo.address = data.address1;
        this.personalInfo.zip = data.zip;
        this.personalInfo.city = data.city;
        this.personalInfo.state = data.state;
        this.personalInfo.country = data.country;
        this.personalInfo.email = data.email;
        this.personalInfo.mobile = data.mobile1;
        this.personalInfo.customerName = data.customerName;
        this.decoded_username = data.firstName //+ " " + data.lastName;
      // } catch (error) {
      //   console.log("ERROR", error);
      //   this.error = error
      // }
    },
  }
})